




import Vue from 'vue'
import { Chart } from 'highcharts-vue'
import Component from 'vue-class-component'
import { XAxisOptions, YAxisOptions } from 'highcharts'
import { generateMatrixSeries, generateNumberIndexSeriesData, generateSlicedSeries, Series, sortSingleSelectSeriesData } from '../Survey/Utilities/ChartUtilities'
import { generateMatrixData, generateMultiselectData, generateNumberData, SingleSelectData } from '@/utilities/highcharts-series-samples'
import { Prop } from 'vue-property-decorator'
import { ColorPreviewType, generateSequentialColors } from '../Survey/Utilities/ColorUtility'

@Component({
  components: {
    Highcharts: Chart
  }
})
export default class ChartPreview extends Vue {
  @Prop({ required: true }) palette!: string[]
  @Prop({ required: true }) type!: ColorPreviewType
  @Prop({ required: false, default: () => 2 }) groupCount!: number
  @Prop({ required: false, default: () => 13 }) barCount!: number

  get chartOptions () {
    return {
      title: {
        text: ''
      },
      chart: {
        type: 'column',
        height: '70%'
      },
      yAxis: this.yAxis,
      series: this.series,
      xAxis: this.xAxis,
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      tooltip: {
        enabled: false
      }
    }
  }

  get series () {
    switch (this.type) {
      case ColorPreviewType.Monochromatic:
        return this.monochromaticSeries
      case ColorPreviewType.Divergent:
        return this.divergingSeries
      case ColorPreviewType.Sequential:
        return this.sequentialSeries
      case ColorPreviewType.Qualitative:
        return this.qualitativeSeries
      default:
        return this.monochromaticSeries
    }
  }

  get monochromaticSeries () {
    return [
      {
        type: 'column',
        visible: true,
        dataLabels: {
          enabled: false
        },
        data: sortSingleSelectSeriesData(SingleSelectData.series, SingleSelectData.questionOptions, this.palette[0]),
        showInLegend: false
      }
    ]
  }

  get MatrixData () {
    return generateMatrixData(this.groupCount, this.barCount)
  }

  get divergingSeries (): Series[] {
    const data = generateMatrixSeries(
      this.MatrixData.totals,
      this.MatrixData.scaleCategories,
      this.MatrixData.displayCategories,
      { hasNaOpt: false, hideLabels: true, colors: this.palette.slice().reverse() }
    )
    return data
  }

  get NumberData () {
    return generateNumberData(this.barCount, true)
  }

  get sequentialSeries () {
    const colors = generateSequentialColors(this.NumberData.categories.length, this.palette[0])
    const data = generateNumberIndexSeriesData(
      this.NumberData.categories,
      this.NumberData.indexTotals,
      this.NumberData.responseLength,
      colors
    )
    return [
      {
        type: 'column',
        visible: true,
        dataLabels: {
          enabled: false
        },
        data,
        showInLegend: false
      }
    ]
  }

  get MultiselectData () {
    return generateMultiselectData(this.groupCount, this.barCount)
  }

  get qualitativeSeries () {
    const seriesData = generateSlicedSeries(
      this.MultiselectData.segmentTotals,
      this.palette,
      this.MultiselectData.sortedCategories,
      { hideLabels: true }
    )
    return seriesData
  }

  get yAxis (): YAxisOptions {
    return {
      min: 0,
      gridLineWidth: 0,
      title: {
        text: ''
      },
      labels: {
        enabled: false
      }
    }
  }

  get xAxis (): XAxisOptions {
    return {
      labels: {
        enabled: false
      },
      tickLength: 0
    }
  }
}
